/**
 * Senders picker for applying rule-based presets.
 * Select from campaign senders or generate with AI (same/per drop).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { filterSenderNamesMax11, uniqueKeepOrderStrings } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

import { SendersListAndPreviews } from './SendersListAndPreviews';
import { SendersSectionHeader } from './SendersSectionHeader';
import { useApplyRuleBasedSenders } from './useApplyRuleBasedSenders';

const MAX_SELECTED_SENDERS = 50;

export interface ApplyRuleBasedSendersSectionProps {
  senderNames: string[];
  selectedSenders: string[];
  onToggleSender: (sender: string) => void;
  onSelectAll: () => void;
  onSendersChange: (senders: string[]) => void;
  onPerDropBatchesChange: (batches: string[][] | null) => void;
  onAiGeneratingChange?: (isGenerating: boolean) => void;
  countryKey: string;
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  isBusy: boolean;
}

export const ApplyRuleBasedSendersSection: FC<ApplyRuleBasedSendersSectionProps> = ({
  senderNames,
  selectedSenders,
  onToggleSender,
  onSelectAll: _onSelectAll,
  onSendersChange,
  onPerDropBatchesChange,
  onAiGeneratingChange,
  countryKey,
  dropCount,
  dropLabels,
  isBusy,
}) => {
  const [search, setSearch] = useState('');
  const {
    ai,
    showAddNew,
    setShowAddNew,
    newSenderValue,
    setNewSenderValue,
    newSenderError,
    setNewSenderError,
    addSenders,
    applyNewSenderInput,
  } = useApplyRuleBasedSenders({
    countryKey,
    campaignSenders: senderNames,
    selectedSenders,
    onSendersChange,
    onPerDropBatchesChange,
    dropCount,
    isBusy,
  });

  const allSenders = useMemo(
    () => uniqueKeepOrderStrings([...senderNames, ...selectedSenders]),
    [senderNames, selectedSenders],
  );

  const filteredSenders = useMemo(() => {
    if (!search.trim()) return allSenders;
    const q = search.toLowerCase();
    return allSenders.filter((s) => s.toLowerCase().includes(q));
  }, [allSenders, search]);

  const handleSelectAllFiltered = useCallback(() => {
    const cap = Math.min(filteredSenders.length, MAX_SELECTED_SENDERS);
    const isDeselect = selectedSenders.length >= cap && selectedSenders.length > 0;
    if (isDeselect) {
      onSendersChange(selectedSenders.filter((s) => !filteredSenders.includes(s)));
    } else {
      onSendersChange(
        filterSenderNamesMax11(
          uniqueKeepOrderStrings([...selectedSenders, ...filteredSenders]),
        ).slice(0, MAX_SELECTED_SENDERS),
      );
    }
  }, [filteredSenders, selectedSenders, onSendersChange]);

  const selectAllLabel =
    selectedSenders.length >= Math.min(filteredSenders.length, MAX_SELECTED_SENDERS) &&
    selectedSenders.length > 0
      ? 'Deselect all'
      : 'Select all';

  const hasAiGenerated = Boolean(
    (ai.perDropBatches?.length ?? 0) > 0 || ai.generatedSenders.length > 0,
  );

  return (
    <section className="flex min-h-0 flex-col gap-2">
      <SendersSectionHeader
        selectedCount={selectedSenders.length}
        selectAllLabel={selectAllLabel}
        onSelectAll={handleSelectAllFiltered}
        isBusy={isBusy}
        filteredCount={filteredSenders.length}
        dropCount={dropCount}
        dropLabels={dropLabels}
        countryKey={countryKey}
        onAiGeneratingChange={onAiGeneratingChange}
        onAiGenerated={ai.handleAiGenerated}
        showAddNew={showAddNew}
        onToggleAddNew={() => setShowAddNew((v) => !v)}
        search={search}
        onSearchChange={setSearch}
        newSenderValue={newSenderValue}
        onNewSenderChange={(v) => {
          setNewSenderValue(v);
          setNewSenderError(null);
        }}
        newSenderError={newSenderError}
        onAddSender={applyNewSenderInput}
        onPasteAddSenders={addSenders}
      />

      <SendersListAndPreviews
        perDropBatches={ai.perDropBatches}
        generatedSenders={ai.generatedSenders}
        dropLabels={dropLabels}
        dropCount={dropCount}
        filteredSenders={filteredSenders}
        selectedSenders={selectedSenders}
        onToggleSender={onToggleSender}
        hasAiGenerated={hasAiGenerated}
        onSavePerDrop={ai.handleSavePerDrop}
        onDismissPerDrop={ai.handleDismissPerDrop}
        onRegeneratePerDrop={ai.handleRegeneratePerDrop}
        onRegenerateAllPerDrop={ai.handleRegenerateAllPerDrop}
        onSaveAndHidePerDrop={ai.handleSaveAndHidePerDrop}
        onSaveGenerated={ai.handleSaveGenerated}
        onDismiss={ai.handleDismiss}
        onRegenerate={ai.handleRegenerate}
        isRegenerating={ai.isRegenerating}
        perDropApplied={ai.perDropApplied}
      />
    </section>
  );
};
